import { extend, forEach } from 'lodash';
import * as ActionTypes from '../constants/ActionTypes';
import * as AppointmentTimesStoreEvents from '../constants/AppointmentTimesStoreEvents';
import * as Constants from '../constants/Constants';
import moment from 'moment';
import parseTimeslots from './utils/parseTimeslots';
import recalculateNextAppointment from './utils/recalculateNextAppointment';
import { Store } from '../utils/flux';

/**
 * @class AppointmentTimesStore
 * @extends Store
 */
export default class AppointmentTimesStore extends Store {
    constructor(options) {
        super(options);

        this.doctorLocationIds = options.doctorLocationIds;
        this.isExistingPatient = options.isExistingPatient;

        this.reset();

        if (options.initialTimes) {
            this.parse(options.initialTimes);
        }
    }

    /**
     *
     * @param {string} doctorLocationId
     * @param {moment} startDate
     * @param {int} numDays
     * @returns {{hasLoaded: boolean, times: object}}
     */
    getByDoctorLocation(doctorLocationId, startDate, numDays) {
        const times = {};

        for (let i = 0; i < numDays; i++) {
            const dateKey = moment(startDate).add(i, 'days').format(Constants.DATE_FORMAT);

            if (!this._timesByDoctorLocation[doctorLocationId]) {
                // this is for debug purposes, feel free to remove once no trouble in CSF tests
                const debugobj = {
                    doctorLocationId,
                    times: this._timesByDoctorLocation,
                };
                window.testdebug = JSON.stringify(debugobj);
            } else {
                times[dateKey] = this._timesByDoctorLocation[doctorLocationId][dateKey];
            }

            if (!times[dateKey]) {
                return {
                    hasLoaded: false,
                    times: undefined,
                };
            }
        }

        return {
            hasLoaded: true,
            times,
        };
    }

    /**
     *
     * @param {string} doctorLocationId
     * @returns {object}
     */
    getByDoctorLocationOnly(doctorLocationId) {
        return this._timesByDoctorLocation[doctorLocationId];
    }

    /**
     * @param {string} timeslotId
     * @returns {object}
     */
    getByTimeslot(timeslotId) {
        return this._timesByTimeslot[timeslotId];
    }

    /**
     *
     * @param doctorLocationId
     * @param date
     * @returns {boolean}
     */
    hasTimesForDate(doctorLocationId, date) {
        const doctorLocationDates = this._timesByDoctorLocation[doctorLocationId];
        const dateKey = date.format(Constants.DATE_FORMAT);

        return !!(doctorLocationDates && doctorLocationDates[dateKey]);
    }

    handleError() {
        this.trigger(AppointmentTimesStoreEvents.ERROR);
    }

    /**
     * @param {object} times
     */
    parse(times) {
        forEach(times, (doctorLocationTimes, doctorLocationId) => {
            extend(this._timesByDoctorLocation[doctorLocationId], doctorLocationTimes);
            extend(this._timesByTimeslot, parseTimeslots(doctorLocationTimes, doctorLocationId));

            recalculateNextAppointment(this._timesByDoctorLocation[doctorLocationId]);
        });

        this.trigger(AppointmentTimesStoreEvents.LOAD_TIMES);
    }

    allTimesReceived() {
        this.trigger(AppointmentTimesStoreEvents.ALL_TIMES_RECEIVED);
    }

    resetDoctorLocationIds() {
        this._timesByDoctorLocation = {};
        this._timesByTimeslot = {};

        this.doctorLocationIds.forEach(doctorLocationId => {
            this._timesByDoctorLocation[doctorLocationId] = {};
        });
    }

    reset() {
        this.resetDoctorLocationIds();
        this.trigger(AppointmentTimesStoreEvents.RESET);
    }

    /**
     * @param {object} payload
     * @param {string} payload.actionType
     * @param {object} [payload.times]
     */
    onDispatch(payload) {
        switch (payload.actionType) {
            case ActionTypes.CHANGE_AVAILABILITY_TIME_FILTER:
            case ActionTypes.CHANGE_INSURANCE:
            case ActionTypes.CHANGE_PROCEDURE:
                this.reset();
                break;

            case ActionTypes.CHANGE_DOCTOR_LOCATION_IDS:
                this.doctorLocationIds = payload.doctorLocationIds;
                this.resetDoctorLocationIds();
                break;

            case ActionTypes.RECEIVE_TIMES:
                if (payload.isExistingPatient === this.isExistingPatient) {
                    this.parse(payload.times);
                }
                break;

            case ActionTypes.SERVER_ERROR:
                this.handleError();
                break;

            case ActionTypes.ALL_TIMES_RECEIVED:
                this.allTimesReceived();
                break;
        }
    }
}
